import { useState } from 'preact/hooks'
import { Check, ChevronLeft, ChevronRight, Plus } from 'lucide-preact'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { InstallPrompt } from '../components/InstallPrompt'
import { NewTaskForm } from '../components/NewTaskForm'
import { isTicked } from '../lib/plan'
import type { DayProgress } from '../lib/plan'
import type { MemberOption } from '../lib/members'
import type { NewChore, TaskTemplate } from '../types'

function prettyDate(date: Date) {
  return format(date, 'EEEE d MMMM', { locale: fr })
}

export function Today({
  date,
  taskDate,
  tasks,
  progress,
  memberId,
  members,
  done,
  onMoveDay,
  onToggle,
  onAddTask,
}: {
  date: Date
  taskDate: string
  tasks: readonly TaskTemplate[]
  progress: DayProgress
  memberId: string
  members: readonly MemberOption[]
  done: ReadonlySet<string>
  onMoveDay: (delta: number) => void
  onToggle: (taskId: string) => void
  /** Resolves to a line worth showing, or null when there is nothing to say. */
  onAddTask: (chore: NewChore) => Promise<string | null>
}) {
  const [adding, setAdding] = useState(false)
  const [notice, setNotice] = useState('')

  return (
    <>
      <section class="date-nav">
        <button class="icon-button" onClick={() => onMoveDay(-1)} aria-label="Jour précédent">
          <ChevronLeft />
        </button>
        <div>
          <strong>{prettyDate(date)}</strong>
          <small>{progress.done}/{progress.total} tâches terminées</small>
        </div>
        <button class="icon-button" onClick={() => onMoveDay(1)} aria-label="Jour suivant">
          <ChevronRight />
        </button>
      </section>

      <section class="card">
        <div class="section-heading">
          <h2>Tâches du jour</h2>
          <span>{progress.percent}%</span>
        </div>

        <div class="task-list">
          {tasks.map((task) => {
            const ticked = isTicked(task.id, taskDate, memberId, done)

            return (
              <button class={`task-row ${ticked ? 'done' : ''}`} onClick={() => onToggle(task.id)}>
                <span class="task-emoji">{task.emoji}</span>
                <span class="task-label">{task.label}</span>
                <span class="task-check">{ticked && <Check size={20} strokeWidth={3} />}</span>
              </button>
            )
          })}
        </div>

        {adding ? (
          <NewTaskForm
            date={date}
            members={members}
            defaultAssigneeId={memberId}
            onAdd={(chore) => {
              // The chore is already in the store by the time this resolves —
              // only the notification can fail, and only that is reported.
              setNotice('')
              void onAddTask(chore).then((problem) => setNotice(problem ?? ''))
            }}
            onClose={() => setAdding(false)}
          />
        ) : (
          <button class="add-task" onClick={() => setAdding(true)}>
            <Plus size={18} />
            Ajouter une tâche
          </button>
        )}

        {notice && <p class="notice">{notice}</p>}
      </section>

      {/* On the screen the app opens on, and nowhere else: it is a one-time
          offer, and *Réglages* is where somebody has already found what they
          were looking for. It renders nothing at all when the app is already
          installed, when the offer has been waved away, or when the browser has
          nothing to give. */}
      <InstallPrompt />
    </>
  )
}
